import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type NumericField, type PricingModeEnum, type SideEnum, toBigWithDefault } from '@talos/kyoko';
import { createNumericField } from './createNumericField';

export interface ModifyFixingEntry {
  rowID: string;
  symbol?: string;
  currency?: string;
  amountCurrency?: string;
  tradedQty?: string;
  tradedSide?: SideEnum;
  PricingReference?: string;
  PricingMode?: PricingModeEnum;
  index?: string;
  fixing?: string;
  price?: string;
  amount?: string;
  fixingField: NumericField;
  priceField: NumericField;
  amountField: NumericField;
}

export interface ModifyFixingState {
  /** The modifiable individual fixings ("fixing entries"), will be rendered in the blotter */
  entries: ModifyFixingEntry[];
  /** For multi-token, there are leg-level prices and a top-level (composite) price. The two are related. This field represents the top level price */
  topLevelPriceField: NumericField;
  /** For multi-token, there are leg-level amounts and a top-level (composite) amount. The two are related. This field represents the top level amount */
  topLevelAmountField: NumericField;
  /** The initial top-level price (taken from the quote) */
  topLevelPrice?: string;
  /** The initial top-level amount (taken from the quote) */
  topLevelAmount?: string;
  /** The top-level pricing reference (taken from the quote). Does not have its own editable field yet, but that'll likely be added shortly. */
  topLevelPricingReference?: string;
}

export const EMPTY_MODIFY_FIXING_STATE: ModifyFixingState = {
  entries: [],
  topLevelPriceField: createNumericField('Price'),
  topLevelAmountField: createNumericField('Amount'),
  topLevelPrice: undefined,
  topLevelAmount: undefined,
  topLevelPricingReference: undefined,
};

const modifyFixingSlice = createSlice({
  name: 'modifyFixingSlice',
  initialState: EMPTY_MODIFY_FIXING_STATE,
  reducers: {
    setTopLevelPrice(state, action: PayloadAction<string | undefined>) {
      state.topLevelPriceField = state.topLevelPriceField.updateValue(action.payload).setTouched(true).validate();
    },
    setTopLevelAmount(state, action: PayloadAction<string | undefined>) {
      state.topLevelAmountField = state.topLevelAmountField.updateValue(action.payload).setTouched(true).validate();
    },
    setEntryFixing(state, action: PayloadAction<{ symbol: string; value: string | undefined }>) {
      state.entries = state.entries.map(entry =>
        entry.symbol !== action.payload.symbol
          ? entry
          : {
              ...entry,
              fixingField: entry.fixingField.updateValue(action.payload.value).setTouched(true).validate(),
            }
      );
    },
    setEntryPrice(state, action: PayloadAction<{ symbol: string; value: string | undefined }>) {
      state.entries = state.entries.map(entry =>
        entry.symbol !== action.payload.symbol
          ? entry
          : {
              ...entry,
              priceField: entry.priceField.updateValue(action.payload.value).setTouched(true).validate(),
            }
      );
    },
    setEntryAmount(state, action: PayloadAction<{ symbol: string; value: string | undefined }>) {
      state.entries = state.entries.map(entry =>
        entry.symbol !== action.payload.symbol
          ? entry
          : {
              ...entry,
              amountField: entry.amountField.updateValue(action.payload.value).setTouched(true).validate(),
            }
      );
    },
    touchAll(state) {
      state.topLevelPriceField = state.topLevelPriceField.setTouched(true).validate();
      state.topLevelAmountField = state.topLevelAmountField.setTouched(true).validate();
      state.entries = state.entries.map(entry => ({
        ...entry,
        fixingField: entry.fixingField.setTouched(true).validate(),
        priceField: entry.priceField.setTouched(true).validate(),
        amountField: entry.amountField.setTouched(true).validate(),
      }));
    },
  },
});

export function hasModifyFixingChanges(state: ModifyFixingState) {
  const topLevelChanged =
    !toBigWithDefault(state.topLevelPriceField.value, 0).eq(toBigWithDefault(state.topLevelPrice, 0)) ||
    !toBigWithDefault(state.topLevelAmountField.value, 0).eq(toBigWithDefault(state.topLevelAmount, 0));

  if (topLevelChanged) {
    return true;
  }

  const someEntryChanged = state.entries.some(
    entry =>
      !toBigWithDefault(entry.fixingField.value, 0).eq(toBigWithDefault(entry.fixing, 0)) ||
      !toBigWithDefault(entry.priceField.value, 0).eq(toBigWithDefault(entry.price, 0)) ||
      !toBigWithDefault(entry.amountField.value, 0).eq(toBigWithDefault(entry.amount, 0))
  );

  return someEntryChanged;
}

export function canModifyFixing(state: ModifyFixingState): { valid: true } | { valid: false; reason: string } {
  if (state.entries.length === 0) {
    return { valid: false, reason: 'No fixing entries available' };
  }

  if (
    state.topLevelPriceField.hasInvalidValue ||
    state.topLevelAmountField.hasInvalidValue ||
    state.entries.some(
      entry =>
        entry.fixingField.hasInvalidValue || entry.priceField.hasInvalidValue || entry.amountField.hasInvalidValue
    )
  ) {
    return { valid: false, reason: 'Invalid fixing values provided' };
  }

  return { valid: true };
}

export const modifyFixingSliceActions = { ...modifyFixingSlice.actions };
export const modifyFixingSliceReducer = modifyFixingSlice.reducer;
